import { Factura } from '../types';
import type { MetodoPagoFactura } from '../shared/metodosPago.js';
import { crearMuestrasDesdeFactura, eliminarMuestrasPorFactura } from './muestrasService';
import httpClient from './httpClient';

export const fetchFacturas = async (queryParams?: string): Promise<{ data: Factura[], pagination?: any }> => {
  try {
    const url = queryParams ? `/api/facturas?${queryParams}` : '/api/facturas';
    const response = await httpClient.get(url);

    // Handle new paginated response format
    if (response.data && response.data.data && Array.isArray(response.data.data)) {
      return {
        data: response.data.data,
        pagination: response.data.pagination
      };
    }

    // Fallback: Handle legacy format (direct array)
    if (Array.isArray(response.data)) {
      return { data: response.data };
    }

    // If no valid format, return empty
    console.warn('Estructura de respuesta inesperada en fetchFacturas:', response.data);
    return { data: [] };
  } catch (error) {
    console.error('Error al obtener facturas:', error);
    throw error;
  }
};

interface ExamenInput {
  examenId: string;
  cantidad: number;
  precioUnitario: number;
}

interface FacturaInput extends Omit<Factura, '_id' | 'numeroFactura' | 'examenes' | 'paciente'> {
  /** Ausente en las facturas a aseguradora (el sujeto es el Seguro). */
  paciente?: string;
  examenes: ExamenInput[];
  /** 'aseguradora' = factura a la ARS; el servidor resuelve el seguro. */
  origen?: 'paciente' | 'aseguradora';
  seguroId?: string;
}

export const createFactura = async (facturaData: FacturaInput): Promise<Factura> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No hay token de autenticación');
    }

    let tokenData: { id?: string };
    try {
      tokenData = JSON.parse(atob(token.split('.')[1]));
    } catch {
      throw new Error('Token inválido o corrupto');
    }

    // Procesar los exámenes antes de enviar
    const examenesProcessed = facturaData.examenes.map(examen => {
      const subtotalOriginal = examen.cantidad * examen.precioUnitario;
      return {
        examen: examen.examenId,
        cantidad: examen.cantidad,
        precioUnitario: examen.precioUnitario,
        subtotalOriginal,
        subtotal: subtotalOriginal
      };
    });
    const subtotalSinDescuento = examenesProcessed.reduce(
      (sum, examen) => sum + examen.subtotalOriginal,
      0
    );

    // Calcular el valor del descuento total
    const descuento = subtotalSinDescuento > 0 ?
      ((subtotalSinDescuento - facturaData.total) / subtotalSinDescuento) * 100 : 0;

    // Simplemente usamos el total proporcionado por el frontend que ya incluye los descuentos
    const facturaPayload = {
      ...facturaData,
      examenes: examenesProcessed,
      subtotalSinDescuento,
      descuentoSeguro: descuento, // Añadir el porcentaje de descuento calculado
      usuario: tokenData.id,
      fecha: facturaData.fecha || new Date(),
      estado: facturaData.estado || 'pendiente'
    };

    const response = await httpClient.post('/api/facturas', facturaPayload);
    const nuevaFactura = response.data;

    // Las facturas a aseguradora son contables: no hay paciente ni muestras.
    if (facturaData.origen !== 'aseguradora') {
      await crearMuestrasDesdeFactura(nuevaFactura._id);
    }

    // Best-effort central sync (ICS-7): `void` is deliberate — syncFactura can
    // never reject and the local write is already committed; a late sync still
    // resolves current state (last-write-wins centrally).
    void syncFactura(nuevaFactura._id);

    return nuevaFactura;
  } catch (error) {
    console.error('Error detallado:', error);
    throw error;
  }
};

export const anularFactura = async (id: string, motivo: string): Promise<Factura> => {
  try {
    // Primero anulamos la factura
    const response = await httpClient.post(`/api/facturas/${id}/anular`, { motivo });
    const facturaAnulada = response.data;

    // Eliminamos las muestras usando la función del servicio
    try {
      await eliminarMuestrasPorFactura(id);
    } catch (error) {
      console.error('Error al eliminar muestras asociadas:', error);
      // Podríamos considerar revertir la anulación de la factura aquí
    }

    // Best-effort central sync (ICS-7): the annulment state rides the upsert.
    void syncFactura(id);

    return facturaAnulada;
  } catch (error) {
    console.error('Error al anular factura:', error);
    throw error;
  }
};

// Detalle de pago del modal (add-cheque-payment-and-cash-change, fase 2):
// el body de POST /api/facturas/:id/imprimir se extiende de {metodoPago} a
// PagoDetalle (contrato del diseño). Solo `metodoPago` es obligatorio — los
// cuerpos legados {metodoPago} siguen siendo válidos en el servidor.
export interface PagoDetalle {
  metodoPago: MetodoPagoFactura;
  cheque?: { numero: string; banco: string; portador: string; fechaCheque?: string };
  montoRecibido?: number; // solo efectivo
  vuelto?: number; // solo efectivo
  fechaVencimiento?: string; // solo crédito (cuenta por cobrar, 'YYYY-MM-DD')
  clienteId?: string; // ID del cliente para facturas a crédito
  clienteNombre?: string; // Nombre del cliente para imprimir en el ticket
}

// add-print-without-payment: el body del flag va SOLO con la marca — la
// impresión sin pago no lleva método ni detalle. Tipos en unión: string
// legado / PagoDetalle / ImpresionSinPago es exclusivo por construcción (no
// se puede pagar y no-pagar en el mismo payload).
export interface ImpresionSinPago {
  sinPago: true;
}

export type ImpresionPayload = string | PagoDetalle | ImpresionSinPago;

export const imprimirFactura = async (facturaId: string, pago: ImpresionPayload) => {
  try {
    // Backward-compatible: string legado (solo metodoPago), PagoDetalle
    // completo o el payload {sinPago:true} — el body builder no cambia.
    const body = typeof pago === 'string' ? { metodoPago: pago } : pago;
    const response = await httpClient.post(`/api/facturas/${facturaId}/imprimir`, body);
    // Print-without-payment mutates nothing (ICS-7) ⇒ no sync. A paid print is
    // the pay transition ⇒ sync best-effort (void: never blocks the ticket —
    // the print path must not inherit httpClient's 30s timeout + retries).
    if (!(typeof pago !== 'string' && 'sinPago' in pago && pago.sinPago === true)) {
      void syncFactura(facturaId);
    }
    return response.data;
  } catch (error) {
    console.error('Error al imprimir factura:', error);
    throw error;
  }
};

export const procesarFactura = async (facturaId: string): Promise<void> => {
  await httpClient.post(`/api/facturas/${facturaId}/procesar`);
};

// Re-transmisión de un e-CF ya emitido (emit-on-create): POST /emisionar sobre
// una factura CON e-NCF reenvía el ledger a la DGII (nunca paga, nunca consume
// de nuevo el e-NCF). El body va vacío — el endpoint decide la rama por encf.
export interface ResultadoEmision {
  exito: boolean;
  factura: Factura;
  advertencia?: string;
}

export const retransmitirFactura = async (facturaId: string): Promise<ResultadoEmision> => {
  try {
    const response = await httpClient.post(`/api/facturas/${facturaId}/emisionar`, {});
    // Best-effort central sync (ICS-7): re-sync on retransmission keeps the
    // central upsert idempotent (last-write-wins by syncTimestamp).
    void syncFactura(facturaId);
    return response.data;
  } catch (error) {
    console.error('Error al retransmitir factura electrónica:', error);
    throw error;
  }
};

// Sincronizar factura con el servidor central — TERCERA vía de sync de salida
// (resultados, muestras, facturas). El body lleva SOLO el id opaco: el payload
// se construye 100% en el servidor proxy desde la base del tenant (la
// identidad de los exámenes nunca es asertable por el cliente).
//
// BEST-EFFORT (ICS-7): el sync corre con `void` (fire-and-forget) en cada
// transición de ciclo de vida (crear / imprimir-pagar / retransmitir / anular)
// y NUNCA rechaza — una caída del servidor central se traga con warn. `void`
// (sin await) es deliberado: el path de impresión de tickets no debe heredar
// el timeout de 30s ni los reintentos con backoff de httpClient (una
// conexión rechazada se reintenta 3× — peor caso ~2 min si se esperara).
// (Patrón de syncResultado en resultadosService.ts; NO copiar el rethrow de
// syncMuestra en muestrasService.ts.)
export const syncFactura = async (facturaId: string): Promise<unknown | null> => {
  try {
    const syncUrl = import.meta.env.VITE_SYNC_FACTURAS_URL;
    if (!syncUrl) {
      console.warn('Sincronización externa deshabilitada: VITE_SYNC_FACTURAS_URL no está definida');
      return null;
    }

    // Verificar si la URL es válida (puede ser relativa o absoluta)
    if (!syncUrl.startsWith('http') && !syncUrl.startsWith('/')) {
      console.warn('Sincronización externa deshabilitada: URL de sincronización inválida');
      return null;
    }

    const response = await httpClient.post(syncUrl, { facturaId });
    return response.data;
  } catch (error) {
    // La sincronización externa no es crítica, solo registramos el error sin lanzar excepción
    console.warn('Advertencia: No se pudo sincronizar la factura con el servidor externo (esto es normal si no está configurado):',
      error instanceof Error ? error.message : 'Error desconocido');
    return null; // Retornamos null en lugar de lanzar error
  }
};

export const updateFactura = async (
  facturaId: string,
  facturaData: any
): Promise<Factura> => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('No hay token de autenticación');
    }

    let facturaPayload = { ...facturaData };

    // Si se actualizan los exámenes, recalcular totales
    if (facturaData.examenes && Array.isArray(facturaData.examenes)) {
      const examenesProcessed = facturaData.examenes.map((examen: any) => {
        const subtotalOriginal = examen.cantidad * examen.precioUnitario;
        return {
          examen: examen.examenId,
          cantidad: examen.cantidad,
          precioUnitario: examen.precioUnitario,
          subtotalOriginal,
          subtotal: subtotalOriginal
        };
      });

      const subtotalSinDescuento = examenesProcessed.reduce(
        (sum: number, examen: any) => sum + examen.subtotalOriginal,
        0
      );

      // Calcular el valor del descuento total al actualizar
      const descuento = subtotalSinDescuento > 0 && facturaData.total ?
        ((subtotalSinDescuento - facturaData.total) / subtotalSinDescuento) * 100 : 0;

      facturaPayload = {
        ...facturaPayload,
        examenes: examenesProcessed,
        subtotalSinDescuento,
        descuentoSeguro: descuento
      };
    }

    const response = await httpClient.put(`/api/facturas/${facturaId}`, facturaPayload);
    const facturaActualizada = response.data;

    // Sincronizar muestras después de actualizar (para exámenes nuevos)
    try {
      await crearMuestrasDesdeFactura(facturaId);
    } catch (muestraError) {
      console.error('Error sincronizando muestras post-actualización:', muestraError);
      // No lanzamos el error para no bloquear la actualización de la factura que ya fue exitosa
    }

    return facturaActualizada;
  } catch (error) {
    console.error('Error al actualizar factura:', error);
    throw error;
  }
};

export const fetchReporteFacturas = async (periodo: string, fechaDesde?: string, fechaHasta?: string) => {
  try {
    let url = `/api/facturas/reporte?periodo=${periodo}`;
    if (fechaDesde && fechaHasta) {
      url += `&fechaDesde=${fechaDesde}&fechaHasta=${fechaHasta}`;
    }
    const response = await httpClient.get(url);
    return response.data;
  } catch (error) {
    console.error('Error al obtener reporte de facturas:', error);
    throw error;
  }
};
